import { actionLoadScene, actionShortcuts } from "../../actions";
import { getShortcutFromShortcutName } from "../../actions/shortcuts";
import { useTunnels } from "../../context/tunnels";
import { useUIAppState } from "../../context/ui-appState";
import { t, useI18n } from "../../i18n";
import { useApp, useEditorInterface, useExcalidrawActionManager } from "../App";
import { ExcalidrawLogo } from "../ExcalidrawLogo";
import { brainIcon, HelpIcon, LoadIcon, usersIcon } from "../icons";

import type { JSX } from "react";

type HeroCopy = {
  heading: string;
  description: string;
  features: string[];
  aiLabel: string;
};

const getHeroCopy = (langCode: string): HeroCopy => {
  const isPortuguese = /^pt(?:-|$)/i.test(langCode);

  if (isPortuguese) {
    return {
      heading: "Organize ideias, mapas e fluxos com IA.",
      description:
        "Escreva, desenhe e deixe a IA organizar a primeira versao. Depois continue editando no mesmo quadro, com colaboracao em tempo real.",
      features: [
        "IA para texto em diagrama",
        "Mapas mentais e processos",
        "Colaboracao em tempo real",
      ],
      aiLabel: "Visao da IA",
    };
  }

  return {
    heading: "Infinite canvas with AI for ideas, maps, and flows.",
    description:
      "Write, draw, and let AI organize the first version. Then keep editing in the same canvas with real-time collaboration.",
    features: [
      "Text-to-diagram AI",
      "Mind maps and workflows",
      "Real-time collaboration",
    ],
    aiLabel: "AI view",
  };
};

const WelcomeScreenMenuItemContent = ({
  icon,
  shortcut,
  children,
}: {
  icon?: JSX.Element;
  shortcut?: string | null;
  children: React.ReactNode;
}) => {
  const editorInterface = useEditorInterface();
  return (
    <>
      <div className="welcome-screen-menu-item__icon">{icon}</div>
      <div className="welcome-screen-menu-item__text">{children}</div>
      {shortcut && editorInterface.formFactor !== "phone" && (
        <div className="welcome-screen-menu-item__shortcut">{shortcut}</div>
      )}
    </>
  );
};
WelcomeScreenMenuItemContent.displayName = "WelcomeScreenMenuItemContent";

const WelcomeScreenMenuItem = ({
  onSelect,
  children,
  icon,
  shortcut,
  className = "",
  ...props
}: {
  onSelect: () => void;
  children: React.ReactNode;
  icon?: JSX.Element;
  shortcut?: string | null;
} & React.ButtonHTMLAttributes<HTMLButtonElement>) => {
  return (
    <button
      {...props}
      type="button"
      className={`welcome-screen-menu-item ${className}`}
      onClick={onSelect}
    >
      <WelcomeScreenMenuItemContent icon={icon} shortcut={shortcut}>
        {children}
      </WelcomeScreenMenuItemContent>
    </button>
  );
};
WelcomeScreenMenuItem.displayName = "WelcomeScreenMenuItem";

const WelcomeScreenMenuItemLink = ({
  children,
  href,
  icon,
  shortcut,
  className = "",
  ...props
}: {
  children: React.ReactNode;
  href: string;
  icon?: JSX.Element;
  shortcut?: string | null;
} & React.AnchorHTMLAttributes<HTMLAnchorElement>) => {
  return (
    <a
      {...props}
      className={`welcome-screen-menu-item ${className}`}
      href={href}
      target="_blank"
      rel="noopener"
    >
      <WelcomeScreenMenuItemContent icon={icon} shortcut={shortcut}>
        {children}
      </WelcomeScreenMenuItemContent>
    </a>
  );
};
WelcomeScreenMenuItemLink.displayName = "WelcomeScreenMenuItemLink";

const FeatureList = ({ items }: { items: string[] }) => {
  return (
    <div className="welcome-screen-center__features">
      {items.map((item) => (
        <span key={item} className="welcome-screen-center__feature">
          {item}
        </span>
      ))}
    </div>
  );
};
FeatureList.displayName = "FeatureList";

const Center = ({ children }: { children?: React.ReactNode }) => {
  const { WelcomeScreenCenterTunnel } = useTunnels();
  const { langCode } = useI18n();
  const app = useApp();
  const hero = getHeroCopy(langCode);
  const isAIEnabled = app.props.aiEnabled !== false;

  return (
    <WelcomeScreenCenterTunnel.In>
      <div className="welcome-screen-center">
        {children || (
          <>
            <Logo />
            <Heading>{hero.heading}</Heading>
            <Description>{hero.description}</Description>
            <FeatureList items={hero.features} />
            <Menu>
              <MenuItemLoadScene />
              {isAIEnabled && <MenuItemAI label={hero.aiLabel} />}
              <MenuItemHelp />
            </Menu>
          </>
        )}
      </div>
    </WelcomeScreenCenterTunnel.In>
  );
};
Center.displayName = "Center";

const Logo = ({ children }: { children?: React.ReactNode }) => {
  return (
    <div className="welcome-screen-center__logo excalifont welcome-screen-decor">
      {children || (
        <>
          <ExcalidrawLogo />
          <div className="welcome-screen-center__logo-copy">
            <span className="welcome-screen-center__logo-name">
              OnePaper Pad
            </span>
          </div>
        </>
      )}
    </div>
  );
};
Logo.displayName = "Logo";

const Heading = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="welcome-screen-center__heading welcome-screen-decor excalifont">
      {children}
    </div>
  );
};
Heading.displayName = "Heading";

const Description = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="welcome-screen-center__description welcome-screen-decor">
      {children}
    </div>
  );
};
Description.displayName = "Description";

const Menu = ({ children }: { children?: React.ReactNode }) => {
  return <div className="welcome-screen-menu">{children}</div>;
};
Menu.displayName = "Menu";

const MenuItemAI = ({ label }: { label: string }) => {
  const app = useApp();

  if (app.props.aiEnabled === false) {
    return null;
  }

  return (
    <WelcomeScreenMenuItem
      shortcut={null}
      onSelect={() =>
        app.setOpenDialog({ name: "ttd", tab: "text-to-diagram" })
      }
      icon={brainIcon}
    >
      {label}
    </WelcomeScreenMenuItem>
  );
};
MenuItemAI.displayName = "MenuItemAI";

const MenuItemHelp = () => {
  const actionManager = useExcalidrawActionManager();

  return (
    <WelcomeScreenMenuItem
      onSelect={() => actionManager.executeAction(actionShortcuts)}
      shortcut="?"
      icon={HelpIcon}
    >
      {t("helpDialog.title")}
    </WelcomeScreenMenuItem>
  );
};
MenuItemHelp.displayName = "MenuItemHelp";

const MenuItemLoadScene = () => {
  const appState = useUIAppState();
  const actionManager = useExcalidrawActionManager();

  if (appState.viewModeEnabled) {
    return null;
  }

  return (
    <WelcomeScreenMenuItem
      onSelect={() => actionManager.executeAction(actionLoadScene)}
      shortcut={getShortcutFromShortcutName("loadScene")}
      icon={LoadIcon}
    >
      {t("buttons.load")}
    </WelcomeScreenMenuItem>
  );
};
MenuItemLoadScene.displayName = "MenuItemLoadScene";

const MenuItemLiveCollaborationTrigger = ({
  onSelect,
}: {
  onSelect: () => any;
}) => {
  const { t } = useI18n();
  return (
    <WelcomeScreenMenuItem shortcut={null} onSelect={onSelect} icon={usersIcon}>
      {t("labels.liveCollaboration")}
    </WelcomeScreenMenuItem>
  );
};
MenuItemLiveCollaborationTrigger.displayName =
  "MenuItemLiveCollaborationTrigger";

// -----------------------------------------------------------------------------

Center.Logo = Logo;
Center.Heading = Heading;
Center.Description = Description;
Center.FeatureList = FeatureList;
Center.Menu = Menu;
Center.MenuItem = WelcomeScreenMenuItem;
Center.MenuItemLink = WelcomeScreenMenuItemLink;
Center.MenuItemAI = MenuItemAI;
Center.MenuItemHelp = MenuItemHelp;
Center.MenuItemLoadScene = MenuItemLoadScene;
Center.MenuItemLiveCollaborationTrigger = MenuItemLiveCollaborationTrigger;

export { Center };
